import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Slideshow notifications component with stacked card effect and auto-cycling.
 * Shows the active card in front and the next card visible behind it.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} notifications - Notification card elements
 *
 * @extends {Component<Refs>}
 */
class SlideshowNotifications extends Component {
  /** @type {number} */
  #current = 0;

  /** @type {number} */
  #count = 0;

  /** @type {number | null} */
  #interval = null;

  /** @type {boolean} */
  #transitioning = false;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #isHovered = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  get #speed() {
    return parseInt(this.getAttribute('autoplay') || '5', 10);
  }

  connectedCallback() {
    super.connectedCallback();

    const notifications = this.querySelectorAll('.slideshow-notification');
    this.#count = notifications.length;

    if (this.#count <= 1) return;

    this.#observeVisibility();
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
  }

  /** Observe visibility to pause autoplay when off-screen */
  #observeVisibility() {
    this.#observer = new IntersectionObserver((entries) => {
      this.#isVisible = /** @type {IntersectionObserverEntry} */ (entries[0]).isIntersecting;
      this.#updateAutoplay();
    });
    this.#observer.observe(this);
  }

  /** Start or stop autoplay based on visibility and hover state */
  #updateAutoplay() {
    this.#stopAutoplay();

    if (this.#isVisible && !this.#isHovered) {
      this.#startAutoplay();
    }
  }

  /** Start auto-cycling notifications */
  #startAutoplay() {
    if (this.#interval) return;
    this.#interval = setInterval(() => this.#next(), this.#speed * 1000);
  }

  /** Stop auto-cycling */
  #stopAutoplay() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  /** Pause on hover */
  #pause = () => {
    this.#isHovered = true;
    this.#updateAutoplay();
  };

  /** Resume after hover */
  #resume = () => {
    this.#isHovered = false;
    this.#updateAutoplay();
  };

  /** Advance to next notification */
  #next() {
    if (this.#transitioning) return;

    const nextIndex = (this.#current + 1) % this.#count;
    this.#transition(nextIndex);
  }

  /**
   * Transition between stacked notifications.
   * Front card exits up, back card moves to front, new back card appears.
   * @param {number} nextIndex - Index of the next notification to show
   */
  #transition(nextIndex) {
    const notifications = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.slideshow-notification'));
    const current = notifications[this.#current];
    const next = notifications[nextIndex];
    const afterNext = notifications[(nextIndex + 1) % this.#count];

    if (!current || !next) return;

    this.#transitioning = true;

    if (prefersReducedMotion()) {
      this.#clearStates(notifications);
      current.setAttribute('aria-hidden', 'true');
      next.classList.add('slideshow-notification--front');
      next.removeAttribute('aria-hidden');

      if (afterNext && afterNext !== next) {
        afterNext.classList.add('slideshow-notification--back');
      }

      this.#current = nextIndex;
      this.#transitioning = false;
      return;
    }

    // Front card exits up
    current.classList.remove('slideshow-notification--front');
    current.classList.add('slideshow-notification--exiting');
    current.setAttribute('aria-hidden', 'true');

    // Back card moves to front
    next.classList.remove('slideshow-notification--back');
    next.classList.add('slideshow-notification--front');
    next.removeAttribute('aria-hidden');

    // New back card appears (if different from next)
    if (afterNext && afterNext !== next && afterNext !== current) {
      afterNext.classList.add('slideshow-notification--back');
    }

    // Wait for exit transition to finish, then snap to back position
    current.addEventListener('transitionend', () => {
      current.classList.remove('slideshow-notification--exiting');

      // With only 2 cards, current becomes the new back instantly (no transition)
      if (this.#count === 2) {
        current.style.transition = 'none';
        current.classList.add('slideshow-notification--back');

        requestAnimationFrame(() => {
          current.style.transition = '';
        });
      }

      this.#current = nextIndex;
      this.#transitioning = false;
    }, { once: true });
  }

  /**
   * Remove all state classes from all notifications
   * @param {NodeListOf<HTMLElement>} notifications
   */
  #clearStates(notifications) {
    for (const notification of notifications) {
      notification.classList.remove(
        'slideshow-notification--front',
        'slideshow-notification--back',
        'slideshow-notification--exiting'
      );
    }
  }
}

if (!customElements.get('slideshow-notifications')) {
  customElements.define('slideshow-notifications', SlideshowNotifications);
}
